import { Link } from '@tanstack/react-router';
import { MailCheck } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { ServerError } from '@/shared/ui/server-error';

import { useForgotPassword } from '../hooks/use-forgot-password';
import { forgotPasswordSchema, type ForgotPasswordValues } from '../schemas/forgot-password-schema';

import { AuthCard } from './auth-frame';

/**
 * Email → POST /platform/v1/auth/password/forgot. The backend answers 202 whether or not the
 * address exists, so the confirmation is deliberately neutral. 429 is the one visible failure.
 */
export function ForgotPasswordCard() {
  const { t } = useTranslation('auth');
  const forgot = useForgotPassword();
  const form = useForm<ForgotPasswordValues>({
    resolver: useI18nResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await forgot.mutateAsync(values);
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  if (forgot.isSuccess) {
    return (
      <AuthCard id="forgot-password" title={t('forgot.sentTitle')}>
        <div role="status" className="flex flex-col gap-4 text-sm">
          <MailCheck className="text-success size-8" aria-hidden />
          <p className="text-muted-foreground leading-relaxed">
            {t('forgot.sentBody', { email: form.getValues('email') })}
          </p>
          <p className="text-muted-foreground text-xs leading-relaxed">{t('forgot.sentHint')}</p>
          <Button asChild variant="outline" className="w-full">
            <Link to="/login">{t('frame.backToSignIn')}</Link>
          </Button>
        </div>
      </AuthCard>
    );
  }

  const rateLimited = forgot.error instanceof ApiError && forgot.error.kind === 'rate_limited';

  return (
    <AuthCard id="forgot-password" title={t('forgot.title')} description={t('forgot.description')}>
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
          {forgot.isError && !forgot.error.hasFieldErrors ? (
            <ServerError
              error={forgot.error}
              message={rateLimited ? t('forgot.rateLimited') : null}
            />
          ) : null}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.email')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder={t('fields.emailPlaceholder')}
                    className="h-11"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            size="lg"
            className="w-full font-semibold"
            disabled={forgot.isPending}
          >
            {forgot.isPending ? t('forgot.submitting') : t('forgot.submit')}
          </Button>
        </form>
      </Form>
    </AuthCard>
  );
}
